'use client';

import React from 'react';
import Link from 'next/link';

// Metadata is now exported as an object (Next.js Metadata API)
// Note: Metadata export must be used in Server Components
// For client components, use dynamic metadata via generateMetadata or move to server component
const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb Navigation */}
      <div className="bg-institutional-grey border-b border-grey-200">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center space-x-2 text-sm" aria-label="Breadcrumb">
            <Link href="/homepage" className="text-institutional hover:text-institutional-dark transition-fast">
              Home
            </Link>
            <span className="text-grey-400">/</span>
            <span className="text-grey-600">Terms of Service</span>
          </nav>
        </div>
      </div>

      {/* Header Section */}
      <div className="bg-institutional text-white py-16 lg:py-20 relative">
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4 text-white">Terms of Service</h1>
          <p className="text-xl text-white max-w-3xl">
            Clear engagement parameters and service boundaries for institutional advisory relationships with Diaax.
          </p>
          <p className="text-sm text-white/70 mt-6">
            Effective Date: December 17, 2025
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 lg:py-16">
        <div className="max-w-4xl mx-auto">
          
          {/* Quick Navigation */}
          <div className="bg-institutional-grey border border-grey-200 rounded-lg p-6 mb-12">
            <h2 className="text-lg font-semibold text-institutional mb-4">Quick Navigation</h2>
            <ul className="space-y-2">
              <li>
                <a href="#section-1" className="text-institutional hover:text-institutional-dark transition-fast">
                  1. Service Scope and Engagement Model
                </a>
              </li>
              <li>
                <a href="#section-2" className="text-institutional hover:text-institutional-dark transition-fast">
                  2. Advisory Relationship Parameters
                </a>
              </li>
              <li>
                <a href="#section-3" className="text-institutional hover:text-institutional-dark transition-fast">
                  3. Intellectual Property Rights
                </a>
              </li>
              <li>
                <a href="#section-4" className="text-institutional hover:text-institutional-dark transition-fast">
                  4. Confidentiality and Data Protection
                </a>
              </li>
              <li>
                <a href="#section-5" className="text-institutional hover:text-institutional-dark transition-fast">
                  5. Professional Standards and Limitations
                </a>
              </li>
              <li>
                <a href="#section-6" className="text-institutional hover:text-institutional-dark transition-fast">
                  6. Payment Terms and Fees
                </a>
              </li>
              <li>
                <a href="#section-7" className="text-institutional hover:text-institutional-dark transition-fast">
                  7. Termination and Amendment
                </a>
              </li>
              <li>
                <a href="#section-8" className="text-institutional hover:text-institutional-dark transition-fast">
                  8. Governing Law and Dispute Resolution
                </a>
              </li>
            </ul>
          </div>

          {/* Introduction */}
          <div className="mb-12 pb-8 border-b border-grey-200">
            <p className="text-grey-700 leading-relaxed">
              These Terms of Service ("Terms") govern the professional advisory relationship between Diaax Ltd ("Diaax," "we," "us," or "our") and institutional clients ("Client," "you," or "your") engaging our strategic advisory and capacity-building services. By initiating contact, requesting consultations, or entering into formal advisory engagements, you acknowledge understanding and acceptance of these Terms.
            </p>
          </div>

          {/* Section 1 */}
          <section id="section-1" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">1. Service Scope and Engagement Model</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">1.1 Advisory Services</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  Diaax provides strategic advisory services focused on institutional transformation within three core domains:
                </p>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li><strong>Strategy and Institutional Design:</strong> Organizational architecture, strategic planning, and structural frameworks</li>
                  <li><strong>Leadership and Capacity Building:</strong> Executive development, team capability enhancement, and organizational learning systems</li>
                  <li><strong>Change and Execution Enablement:</strong> Implementation support, transformation management, and operational optimization</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">1.2 Engagement Framework</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  Professional engagements typically proceed through a structured methodology:
                </p>
                <ol className="list-decimal list-inside space-y-2 text-grey-700 ml-4">
                  <li><strong>Discovery Phase:</strong> Institutional context assessment and strategic challenge identification</li>
                  <li><strong>Design Phase:</strong> Solution architecture and approach customization</li>
                  <li><strong>Execution Phase:</strong> Implementation support and capability transfer</li>
                  <li><strong>Integration Phase:</strong> Sustainability mechanisms and ongoing optimization</li>
                </ol>
                <p className="text-grey-700 leading-relaxed mt-4">
                  Specific engagement parameters, deliverables, timelines, and fees are detailed in formal advisory agreements supplementing these Terms.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">1.3 Service Delivery</h3>
                <p className="text-grey-700 leading-relaxed">
                  Advisory services are delivered with professional expertise and relationship continuity throughout institutional transformation journeys.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section id="section-2" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">2. Advisory Relationship Parameters</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">2.1 Client Responsibilities</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  Successful institutional transformation works best with client commitment to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li>Providing accurate, complete information about organizational context and challenges</li>
                  <li>Ensuring appropriate stakeholder engagement and decision-maker access</li>
                  <li>Allocating sufficient internal resources for implementation activities</li>
                  <li>Maintaining transparent communication regarding constraints and evolving priorities</li>
                  <li>Honoring agreed timelines and participation requirements</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">2.2 Diaax Commitments</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  We aim to deliver:
                </p>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li>Strategic guidance grounded in systematic methodology</li>
                  <li>Customized approaches aligned with institutional context and culture</li>
                  <li>Evidence-based recommendations supported by analysis</li>
                  <li>Transparent communication regarding progress, challenges, and adjustments</li>
                  <li>Professional standards consistent with government and development sector expectations</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">2.3 Collaborative Framework</h3>
                <p className="text-grey-700 leading-relaxed">
                  Our advisory model emphasizes partnership. We work alongside institutional leadership and teams to build internal capability while providing external expertise, supporting sustainable transformation beyond advisory engagement duration.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section id="section-3" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">3. Intellectual Property Rights</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">3.1 Client-Specific Deliverables</h3>
                <p className="text-grey-700 leading-relaxed">
                  Upon full payment, clients receive ownership of customized deliverables created specifically for their institutional context, including strategic frameworks, organizational designs, implementation plans, and related documentation. These materials may be used, adapted, and shared within the client organization as appropriate.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">3.2 Diaax Intellectual Property</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  Diaax retains ownership of:
                </p>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li>Core methodologies, frameworks, and analytical tools</li>
                  <li>General templates and assessment instruments</li>
                  <li>Proprietary diagnostic approaches and intervention models</li>
                  <li>Training materials and capacity-building content (general versions)</li>
                </ul>
                <p className="text-grey-700 leading-relaxed mt-4">
                  Clients receive non-exclusive licenses to utilize Diaax methodologies within their organizations but may not commercialize, license, or distribute these approaches to external parties without written permission.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">3.3 Knowledge Sharing</h3>
                <p className="text-grey-700 leading-relaxed">
                  Diaax may develop generalized case studies, insights, or thought leadership content based on engagement experiences, provided all client-specific information is anonymized and institutional confidentiality is maintained. Clients will be consulted before any such publications involving their engagements.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section id="section-4" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">4. Confidentiality and Data Protection</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">4.1 Mutual Confidentiality</h3>
                <p className="text-grey-700 leading-relaxed">
                  Both parties maintain confidentiality of proprietary, strategic, and sensitive information shared during advisory engagements, following standard professional practice. This obligation extends beyond engagement termination and covers internal processes, financial data, strategic plans, personnel matters, and other institutional information not publicly available.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">4.2 Data Protection Compliance</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  Diaax handles client data following applicable data protection regulations, including GDPR principles where relevant. Our data practices include:
                </p>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li>Secure storage and transmission of information</li>
                  <li>Access controls appropriate to engagement requirements</li>
                  <li>Retention periods aligned with professional standards</li>
                  <li>Standard protocols for data handling throughout engagement lifecycle</li>
                </ul>
                <p className="text-grey-700 leading-relaxed mt-4">
                  Detailed data protection practices are outlined in our Privacy Policy, incorporated by reference into these Terms.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">4.3 Exceptions</h3>
                <p className="text-grey-700 leading-relaxed">
                  Confidentiality obligations do not apply to information that: (a) becomes publicly available through no breach by either party; (b) was possessed prior to disclosure; (c) is independently developed; or (d) must be disclosed pursuant to legal requirements, provided the disclosing party provides reasonable advance notice where permitted.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section id="section-5" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">5. Professional Standards and Limitations</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">5.1 Advisory Nature of Services</h3>
                <p className="text-grey-700 leading-relaxed">
                  Diaax provides strategic advisory and capacity-building services. Final decisions regarding institutional strategy, organizational design, personnel matters, and implementation approaches remain the responsibility of client leadership. Our recommendations represent professional judgment based on analysis and experience.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">5.2 Scope Limitations</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  Unless explicitly stated in formal engagement agreements, Diaax services do not include:
                </p>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li>Legal counsel or regulatory compliance advice</li>
                  <li>Financial audit or accounting services</li>
                  <li>Technical IT implementation or system development</li>
                  <li>Direct operational management or day-to-day supervision</li>
                  <li>Recruitment services or personnel placement</li>
                </ul>
                <p className="text-grey-700 leading-relaxed mt-4">
                  Where specialized expertise beyond our scope is needed, we will recommend engaging appropriate professionals.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">5.3 Limitation of Liability</h3>
                <p className="text-grey-700 leading-relaxed">
                  To the extent permitted by law, Diaax's liability for claims arising from advisory engagements is limited to the fees paid for the specific engagement phase giving rise to the claim. This limitation reflects the advisory nature of our services and the client-controlled nature of implementation decisions.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section id="section-6" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">6. Payment Terms and Fees</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">6.1 Fee Structure</h3>
                <p className="text-grey-700 leading-relaxed">
                  Advisory fees are detailed in formal engagement agreements and may be structured as project-based fees, retainer arrangements, or hybrid models depending on engagement scope and duration. Fee determinations reflect engagement complexity, institutional context, and required time commitment.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">6.2 Payment Schedule</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  Standard payment terms include:
                </p>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li>Initial deposit upon engagement agreement execution (typically 30-50%)</li>
                  <li>Phase-based payments aligned with deliverable completion</li>
                  <li>Final payment upon engagement conclusion and deliverable acceptance</li>
                  <li>Net 30 days for invoice settlement unless otherwise agreed</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">6.3 Expenses</h3>
                <p className="text-grey-700 leading-relaxed">
                  Reasonable out-of-pocket expenses (travel, accommodation, materials) incurred in service delivery are billed separately with supporting documentation. Expense budgets and approval processes are established in engagement agreements.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">6.4 Late Payment</h3>
                <p className="text-grey-700 leading-relaxed">
                  Overdue balances may incur interest charges at prevailing commercial rates. Persistent payment delays may result in engagement suspension until accounts are current, without relieving payment obligations for work already completed.
                </p>
              </div>
            </div>
          </section>

          {/* Section 7 */}
          <section id="section-7" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">7. Termination and Amendment</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">7.1 Termination by Either Party</h3>
                <p className="text-grey-700 leading-relaxed">
                  Either party may terminate advisory engagements with 30 days written notice. Upon termination, clients remain obligated to pay for services rendered through the termination date plus any committed deliverables in progress. Diaax will provide transition support to minimize disruption where possible.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">7.2 Immediate Termination</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  Either party may terminate immediately if:
                </p>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li>The other party materially breaches these Terms and fails to cure within 15 days of written notice</li>
                  <li>The other party becomes subject to insolvency proceedings</li>
                  <li>Continuing the engagement becomes unlawful or professionally unethical</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">7.3 Term Modifications</h3>
                <p className="text-grey-700 leading-relaxed">
                  Diaax may update these Terms periodically to reflect evolving practices, legal requirements, or service offerings. Material changes will be communicated with notice. Continued engagement following such changes constitutes acceptance of revised Terms. Engagement-specific agreements supersede these general Terms where conflicts exist.
                </p>
              </div>
            </div>
          </section>

          {/* Section 8 */}
          <section id="section-8" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">8. Governing Law and Dispute Resolution</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">8.1 Governing Law</h3>
                <p className="text-grey-700 leading-relaxed">
                  These Terms are governed by the laws of England and Wales. Where clients are located in other jurisdictions, specific engagement agreements may specify alternative governing law or incorporate local legal requirements as appropriate.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">8.2 Dispute Resolution Process</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  In the event of disputes, parties will:
                </p>
                <ol className="list-decimal list-inside space-y-2 text-grey-700 ml-4">
                  <li><strong>Good Faith Negotiation:</strong> Attempt resolution through direct discussion</li>
                  <li><strong>Mediation:</strong> If negotiation fails, engage neutral mediation before pursuing formal proceedings</li>
                  <li><strong>Arbitration or Litigation:</strong> After exhausting alternative resolution mechanisms</li>
                </ol>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">8.3 Jurisdiction</h3>
                <p className="text-grey-700 leading-relaxed">
                  Subject to alternative dispute resolution processes described above, parties consent to the jurisdiction of courts in England and Wales for legal proceedings related to these Terms.
                </p>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section id="section-9" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">9. Contact and Inquiries</h2>
            
            <div className="bg-institutional-grey border border-grey-200 rounded-lg p-6 space-y-4">
              <p className="text-grey-700 leading-relaxed">
                For questions about these Terms of Service, engagement inquiries, or clarifications regarding advisory service parameters:
              </p>
              
              <div className="space-y-2">
                <p className="text-institutional-dark font-semibold">Diaax Ltd</p>
                <p className="text-grey-700">Strategy & Capacity Building for Institutional Transformation</p>
                <p className="text-grey-700">
                  Email: <a href="mailto:info@diaax.com" className="text-institutional hover:text-institutional-dark underline">info@diaax.com</a>
                </p>
                <p className="text-grey-700">
                  Website: <Link href="/homepage" className="text-institutional hover:text-institutional-dark underline">www.diaax.com</Link>
                </p>
              </div>

              <p className="text-grey-700 leading-relaxed text-sm mt-4">
                We welcome dialogue with institutional leaders exploring strategic transformation and capacity-building partnerships. Initial consultations help determine engagement fit and approach alignment.
              </p>
            </div>
          </section>

          {/* Acknowledgment */}
          <div className="border-t border-grey-200 pt-8 mt-12">
            <h3 className="text-xl font-semibold text-institutional-dark mb-4">Acknowledgment and Acceptance</h3>
            <p className="text-grey-700 leading-relaxed">
              By engaging Diaax's services, submitting consultation requests, or entering into formal advisory agreements, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. These Terms complement specific engagement agreements and our Privacy Policy to establish the complete framework for professional institutional advisory relationships with Diaax.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;